import React, { useEffect } from 'react';

import { Radio } from 'components';
import { useTranslation } from 'react-i18next';
import { TranslationKeys } from 'localization';
import { getTimeZoneByGeoLocation } from 'async-timing';
import { DatePickerButton, Flex, H5, P } from 'design-system-web';
import { useChannelAdminContext } from 'hooks';
import styles from './DraftChargeInvoiceOptions.scss';
import { InvoiceType } from 'graphql-query-contracts';
import { useForm, Controller, useWatch } from 'react-hook-form';

const CHARGE_TYPE_FIELD = 'invoiceType';
const INVOICE_DUE_DATE_FIELD = 'invoiceDueDate';

const TRANSLATION_KEYS: { [key: string]: TranslationKeys } = Object.freeze({
  invoiceOptionsHeader:
    'abp.charges.chargeManager.draftCharge.submitModal.invoicingOptions.title',
  invoiceOptionsDescription:
    'abp.charges.chargeManager.draftCharge.submitModal.invoicingOptions.description',
  monthlyInvoiceLabel:
    'abp.charges.chargeManager.draftCharge.submitModal.invoicingOptions.addToMonthlyInvoice',
  oneOffInvoiceLabel:
    'abp.charges.chargeManager.draftCharge.submitModal.invoicingOptions.createNewInvoice',
  oneOffInvoiceDueDateLabel:
    'abp.charges.chargeManager.draftCharge.submitModal.invoicingOptions.selectDueDate',
  noInvoiceLabel:
    'abp.charges.chargeManager.draftCharge.submitModal.invoicingOptions.donotInvoice',
});

interface FormData {
  invoiceType: InvoiceType;
  invoiceDueDate?: Date;
}

export function DraftChargeInvoiceOptions({
  updateChargeDetails,
  invoiceType = InvoiceType.InvoiceTypeMonthly,
  onValidationChange,
}: {
  updateChargeDetails: (value: InvoiceType | Date, fieldname: string) => void;
  invoiceType?: InvoiceType;
  onValidationChange?: (isValid: boolean) => void;
}) {
  const { t } = useTranslation();
  const { channel } = useChannelAdminContext();
  
  const { control } = useForm<FormData>({
    defaultValues: {
      invoiceType,
      invoiceDueDate: undefined,
    },
  });

  // Watch the invoice type and due date to validate form
  const selectedInvoiceType = useWatch({
    control,
    name: 'invoiceType',
  });

  const selectedDueDate = useWatch({
    control,
    name: 'invoiceDueDate',
  });

  const isOneOffOptionSelected = selectedInvoiceType === InvoiceType.InvoiceTypeOneoff;
  const isDateSelected = Boolean(selectedDueDate);
  const isValid = !isOneOffOptionSelected || (isOneOffOptionSelected && isDateSelected);

  useEffect(() => {
    onValidationChange?.(isValid);
  }, [isValid, onValidationChange]);

  const timeZone = channel?.address?.geo.length === 2
    ? getTimeZoneByGeoLocation({
        longitude: channel.address.geo[0],
        latitude: channel.address.geo[1],
      })
    : '';

  const minInvoiceDueDateIsToday = new Date();

  return (
    <Flex direction="column" className={styles.optionsContainer}>
      <H5>{t(TRANSLATION_KEYS.invoiceOptionsHeader)}</H5>
      <P className={styles.description}>
        {t(TRANSLATION_KEYS.invoiceOptionsDescription)}
      </P>
      
      <Controller
        control={control}
        name="invoiceType"
        render={({ field: { onChange, value } }) => {
          const handleRadioChange = (newValue: InvoiceType) => {
            onChange(newValue);
            updateChargeDetails(newValue, CHARGE_TYPE_FIELD);
          };

          return (
            <div>
              <Radio
                text={TRANSLATION_KEYS.monthlyInvoiceLabel}
                name={CHARGE_TYPE_FIELD}
                value={InvoiceType.InvoiceTypeMonthly}
                selected={value}
                onChange={handleRadioChange}
                testId="AddToMonthlyInvoiceRadio"
              />
              <Radio
                className={
                  !isOneOffOptionSelected && styles.chargeInvoiceDueDateFieldNotVisible
                }
                text={TRANSLATION_KEYS.oneOffInvoiceLabel}
                name={CHARGE_TYPE_FIELD}
                value={InvoiceType.InvoiceTypeOneoff}
                selected={value}
                onChange={handleRadioChange}
                testId="OneOffInvoiceRadio"
              />
              
              {isOneOffOptionSelected && (
                <Controller
                  control={control}
                  name="invoiceDueDate"
                  render={({ field: { onChange: onDateChange, value: dateValue } }) => (
                    <DatePickerButton
                    className={styles.date}
                    wrapperClassName={styles.fullWidth}
                    buttonClassName={styles.fullWidth}
                      dateInputLabel={TRANSLATION_KEYS.oneOffInvoiceDueDateLabel}
                      value={dateValue}
                      onChange={(newDate: Date) => {
                        onDateChange(newDate);
                        updateChargeDetails(newDate, INVOICE_DUE_DATE_FIELD);
                      }}
                      hideOnSelect
                      minDate={minInvoiceDueDateIsToday}
                      timeZone={timeZone}
                    />
                  )}
                />
              )}
              
              <Radio
                text={TRANSLATION_KEYS.noInvoiceLabel}
                name={CHARGE_TYPE_FIELD}
                value={InvoiceType.InvoiceTypeNoInvoice}
                selected={value}
                onChange={handleRadioChange}
                testId="NoInvoiceRadio"
              />
            </div>
          );
        }}
      />
    </Flex>
  );
}
